import React from "react";
import styles from "./AddPaystubModal.module.css";
import {
  parseCsv,
  buildHeaderMap,
  normalizeCsvRow,
  unmappedHeaders,
} from "../../utils/csvUtils";
import { computeStatutoryDeductions, moneyFmt } from "../../utils/deductions";
import { kvFromObject, ensureKvArray, sumKV } from "../../utils/keyValue";

/**
 * Props:
 *  - onClose, onSubmit(doc), onImport(docs)
 *  - configIndex
 *  - year, jurisdiction { countryCode, provinceOrState, filingStatus }
 *  - onJurisdictionChange?: (next) => void
 *  - userEmail (string), source ({id,name})
 */
export default function AddPaystubModal({
  onClose, onSubmit, onImport,
  configIndex, year: initialYear, jurisdiction, onJurisdictionChange,
  userEmail = "", source = { id: "", name: "" },
}) {
  const [tab, setTab] = React.useState("single");

  // local jurisdiction
  const [year, setYear] = React.useState(initialYear);
  const [countryCode, setCountryCode] = React.useState(jurisdiction?.countryCode || "ca");
  const [regionCode, setRegionCode] = React.useState(jurisdiction?.provinceOrState || "ab");
  const [filingStatus, setFilingStatus] = React.useState(jurisdiction?.filingStatus || "single");

  React.useEffect(() => { setYear(initialYear); }, [initialYear]);
  React.useEffect(() => {
    setCountryCode(jurisdiction?.countryCode || "ca");
    setRegionCode(jurisdiction?.provinceOrState || "ab");
    setFilingStatus(jurisdiction?.filingStatus || "single");
  }, [jurisdiction?.countryCode, jurisdiction?.provinceOrState, jurisdiction?.filingStatus]);

  React.useEffect(() => {
    onJurisdictionChange?.({ countryCode, provinceOrState: regionCode, filingStatus });
  }, [countryCode, regionCode, filingStatus, onJurisdictionChange]);

  // options
  const countryOptions = React.useMemo(() => {
    if (!configIndex) return [];
    return (configIndex.countries || []).map((c) => ({
      value: String(c.code || "").toLowerCase(),
      label: (c.flag ? `${c.flag} ` : "") + (c.name || String(c.code || "").toUpperCase()),
    }));
  }, [configIndex]);

  const yearOptions = React.useMemo(() => {
    if (!configIndex) return [];
    return configIndex.listYearsForCountry(countryCode).map((y) => ({ value: y, label: String(y) }));
  }, [configIndex, countryCode]);

  const regionOptions = React.useMemo(() => {
    if (!configIndex) return [];
    return configIndex.listRegionsForCountry(countryCode).map((r) => ({
      value: r,
      label: configIndex.regionLabel(countryCode, r) || r.toUpperCase(),
    }));
  }, [configIndex, countryCode]);

  React.useEffect(() => {
    if (yearOptions.length && !yearOptions.some((o) => o.value === year)) {
      setYear(yearOptions[0]?.value || new Date().getFullYear());
    }
  }, [countryCode, yearOptions, year]);

  React.useEffect(() => {
    if (regionOptions.length && !regionOptions.some((o) => o.value === regionCode)) {
      setRegionCode(regionOptions[0]?.value);
    }
  }, [countryCode, regionOptions, regionCode]);

  /* ========================== Single Entry ========================== */
  const [form, setForm] = React.useState({
    payDate: "",
    periodStart: "",
    periodEnd: "",
    gross: "",
    taxes: { federal: "", provincial: "", state: "", territorial: "" },
    otherDeductions: { ei: "", cpp: "", cpp2: "", rrsp: "", benefits: "", misc: "" },
    takeHome: "",
  });

  React.useEffect(() => {
    if (!configIndex) return;
    const defs = configIndex.pickOtherDeductions({ countryCode, year });
    const g = Number(form.gross) || 0;

    const { ei, cpp, cpp2 } = computeStatutoryDeductions({
      grossForThisPay: g,
      ytdBeforeThisPay: 0,
      otherDeductionDefs: defs,
    });

    setForm((f) => ({
      ...f,
      otherDeductions: {
        ei: f.otherDeductions.ei || (ei ? round2(ei) : ""),
        cpp: f.otherDeductions.cpp || (cpp ? round2(cpp) : ""),
        cpp2: f.otherDeductions.cpp2 || (cpp2 ? round2(cpp2) : ""),
        rrsp: f.otherDeductions.rrsp || "",
        benefits: f.otherDeductions.benefits || "",
        misc: f.otherDeductions.misc || "",
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configIndex, countryCode, year, form.gross]);

  const taxesSum =
    Number(form.taxes.federal||0) + Number(form.taxes.provincial||0) +
    Number(form.taxes.state||0) + Number(form.taxes.territorial||0);

  const otherSum =
    ["ei","cpp","cpp2","rrsp","benefits","misc"]
      .reduce((a,k)=>a + (Number(form.otherDeductions[k])||0),0);

  const computedTakeHome = (Number(form.gross)||0) - taxesSum - otherSum;

  const normalizeEmail = (e) => String(e || "").trim().toLowerCase();

  const submitSingle = (e) => {
    e.preventDefault();
    // FINAL FIRESTORE DOC (single)
    const doc = {
      userEmail: normalizeEmail(userEmail),
      sourceId: source?.id || "",
      sourceName: source?.name || "",
      jurisdiction: { countryCode, provinceOrState: regionCode, filingStatus, year },
      payDate: form.payDate || null,
      periodStart: form.periodStart || null,
      periodEnd: form.periodEnd || null,
      gross: toNum(form.gross),
      taxes: kvFromObject(form.taxes),                // [{key,value}]
      otherDeductions: kvFromObject(form.otherDeductions), // [{key,value}]
      takeHome: form.takeHome === "" ? computedTakeHome : toNum(form.takeHome),
    };

    try {
      console.groupCollapsed("%c[InTax][Single] Firestore doc", "color:#10b981;font-weight:700");
      console.log(doc);
      console.groupEnd();
    } catch {}

    onSubmit?.(doc);
  };

  /* =========================== CSV Import =========================== */
  const [rows, setRows] = React.useState([]);       // preview rows (not stamped)
  const [err, setErr] = React.useState("");
  const [fileName, setFileName] = React.useState("");
  const [headerDebug, setHeaderDebug] = React.useState({ unknown: [], mappedCount: 0 });

  const onFile = async (e) => {
    setErr(""); setRows([]); setHeaderDebug({ unknown: [], mappedCount: 0 });
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (!parsed.headers?.length) {
        setErr("Could not read headers. Ensure the first row has column names.");
        return;
      }
      const map = buildHeaderMap(parsed.headers);
      const unknown = unmappedHeaders(parsed.headers, map);
      setHeaderDebug({ unknown, mappedCount: Object.values(map).filter(Boolean).length });

      const normalized = parsed.rows.map((r, idx) => {
        const base = normalizeCsvRow(r, map, idx);  // may output objects
        return {
          ...base,
          taxes: ensureKvArray(base.taxes),
          otherDeductions: ensureKvArray(base.otherDeductions),
        };
      });
      setRows(normalized);
    } catch (ex) {
      setErr(`Failed to read CSV: ${String(ex?.message || ex)}`);
    }
  };

  const handleImportClick = () => {
    // FINAL FIRESTORE DOCS (bulk)
    const docs = rows.map((r) => ({
      userEmail: normalizeEmail(userEmail),
      sourceId: source?.id || "",
      sourceName: source?.name || "",
      jurisdiction: { countryCode, provinceOrState: regionCode, filingStatus, year },
      payDate: r.payDate || null,
      periodStart: r.periodStart || null,
      periodEnd: r.periodEnd || null,
      gross: Number(r.gross) || 0,
      taxes: ensureKvArray(r.taxes),                 // [{key,value}]
      otherDeductions: ensureKvArray(r.otherDeductions), // [{key,value}]
      takeHome: r.takeHome ?? ((Number(r.gross)||0) - sumKV(r.taxes) - sumKV(r.otherDeductions)),
    }));

    try {
      console.groupCollapsed(
        `%c[InTax][Import] Firestore docs — ${docs.length}`,
        "color:#10b981;font-weight:700"
      );
      console.log(docs);
      console.groupEnd();
    } catch {}

    onImport?.(docs);
  };

  const hasRows = rows.length > 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`modal-backdrop fade show pp-modal-backdrop ${styles.backdrop}`}
        style={{ zIndex: 1990 }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`modal fade show d-block pp-modal ${styles.modal}`}
        style={{ zIndex: 2000 }}
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e)=>e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add pay stubs</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
            </div>

            <div className="modal-body">
              {/* Jurisdiction row */}
              <div className="row g-2 mb-3">
                <div className="col-12 col-md-3">
                  <label className="form-label">Country</label>
                  <select className="form-select" value={countryCode} onChange={(e)=>setCountryCode(e.target.value)}>
                    {countryOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Province/State</label>
                  <select className="form-select" value={regionCode} onChange={(e)=>setRegionCode(e.target.value)}>
                    {regionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label">Year</label>
                  <select className="form-select" value={year} onChange={(e)=>setYear(Number(e.target.value))}>
                    {yearOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="col-6 col-md-4">
                  <label className="form-label">Filing status</label>
                  <select className="form-select" value={filingStatus} onChange={(e)=>setFilingStatus(e.target.value)}>
                    <option value="single">Single</option>
                    <option value="joint">Married filing jointly</option>
                  </select>
                </div>
              </div>

              {/* Tabs */}
              <ul className="nav nav-tabs">
                <li className="nav-item">
                  <button className={`nav-link ${tab==='single'?'active':''}`} onClick={()=>setTab('single')}>
                    Single entry
                  </button>
                </li>
                <li className="nav-item">
                  <button className={`nav-link ${tab==='csv'?'active':''}`} onClick={()=>setTab('csv')}>
                    Import CSV
                  </button>
                </li>
              </ul>

              <div className="pt-3">
                {tab === "single" ? (
                  <form onSubmit={submitSingle}>
                    <h6 className="mb-2">Income — Gross</h6>
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-4">
                        <label className="form-label">Pay date</label>
                        <input type="date" className="form-control" value={form.payDate}
                          onChange={(e)=>setForm(f=>({...f, payDate: e.target.value}))}/>
                      </div>
                      <div className="col-6 col-md-4">
                        <label className="form-label">Period start</label>
                        <input type="date" className="form-control" value={form.periodStart}
                          onChange={(e)=>setForm(f=>({...f, periodStart: e.target.value}))}/>
                      </div>
                      <div className="col-6 col-md-4">
                        <label className="form-label">Period end</label>
                        <input type="date" className="form-control" value={form.periodEnd}
                          onChange={(e)=>setForm(f=>({...f, periodEnd: e.target.value}))}/>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Gross amount</label>
                        <input type="number" className="form-control" inputMode="decimal" value={form.gross}
                          onChange={(e)=>setForm(f=>({...f, gross: e.target.value}))}/>
                      </div>
                    </div>

                    <h6 className="mb-2">Deductions — Taxes</h6>
                    <div className="row g-3 mb-3">
                      {["federal","provincial","state","territorial"].map((k)=>(
                        <div key={k} className="col-6 col-md-3">
                          <label className="form-label text-capitalize">{k}</label>
                          <input type="number" className="form-control" inputMode="decimal"
                            value={form.taxes[k]}
                            onChange={(e)=>setForm(f=>({...f, taxes:{...f.taxes,[k]: e.target.value}}))}/>
                        </div>
                      ))}
                    </div>

                    <h6 className="mb-2">Other deductions</h6>
                    <div className="row g-3">
                      {["ei","cpp","cpp2","rrsp","benefits","misc"].map((k)=>(
                        <div key={k} className="col-6 col-md-4">
                          <label className="form-label text-uppercase">{k}</label>
                          <input type="number" className="form-control" inputMode="decimal"
                            value={form.otherDeductions[k]}
                            onChange={(e)=>setForm(f=>({...f, otherDeductions:{...f.otherDeductions,[k]: e.target.value}}))}/>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3 d-flex gap-2">
                      <div className="flex-grow-1" style={{maxWidth: 360}}>
                        <label className="form-label">Take-home (optional)</label>
                        <input type="number" className="form-control" inputMode="decimal"
                          placeholder={`${computedTakeHome} (computed)`}
                          value={form.takeHome}
                          onChange={(e)=>setForm(f=>({...f, takeHome: e.target.value}))}/>
                        <div className="form-text">Leave blank to auto-compute.</div>
                      </div>

                      <div className="align-self-end">
                        <button type="submit" className="btn btn-primary">Add stub</button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <div>
                    <div className="mb-3">
                      <label className="form-label">Upload CSV</label>
                      <input type="file" className="form-control" accept=".csv,text/csv" onChange={onFile} />
                      <div className="form-text">
                        Parsed using your selected jurisdiction/year; you can still edit after import.
                      </div>
                    </div>

                    {(headerDebug.unknown.length > 0) && (
                      <div className="alert alert-warning py-2">
                        <div className="small">
                          Unrecognized columns: <strong>{headerDebug.unknown.join(", ")}</strong>
                        </div>
                      </div>
                    )}

                    {err && <div className="alert alert-danger">{err}</div>}

                    {hasRows && (
                      <>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="small text-secondary">
                            Previewing <strong>{rows.length}</strong> rows {fileName && <>from <em>{fileName}</em></>}.
                          </div>
                          <button className="btn btn-primary btn-compact" onClick={handleImportClick}>
                            Import {rows.length} stubs
                          </button>
                        </div>

                        <div className="pp-table is-cozy">
                          <div className="pp-table-scroll">
                            <table className="table table-striped align-middle">
                              <thead>
                                <tr>
                                  <th>Pay date</th>
                                  <th>Period</th>
                                  <th className="is-num">Gross</th>
                                  <th className="is-num">Tax (withheld)</th>
                                  <th className="is-num">Other</th>
                                  <th className="is-num">Take-home</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((r) => {
                                  const taxSum   = sumKV(r.taxes);
                                  const otherSum = sumKV(r.otherDeductions);
                                  const takeHome = r.takeHome ?? ((Number(r.gross) || 0) - taxSum - otherSum);
                                  return (
                                    <tr key={r.__rowId}>
                                      <td>{r.payDate || "—"}</td>
                                      <td className="truncate">{r.periodStart || "—"} → {r.periodEnd || "—"}</td>
                                      <td className="is-num">{moneyFmt.format(Number(r.gross) || 0)}</td>
                                      <td className="is-num">{moneyFmt.format(taxSum)}</td>
                                      <td className="is-num">{moneyFmt.format(otherSum)}</td>
                                      <td className="is-num">{moneyFmt.format(takeHome)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* helpers */
function toNum(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }
function round2(n) { return Math.round(Number(n || 0) * 100) / 100; }
